import type { ActivatedRouteSnapshot } from '@angular/router';

/**
 * Matches a well-formed RFC 4122/9562 GUID: 8-4-4-4-12 hex digits, version nibble `1`-`8`,
 * RFC4122 variant nibble `8`-`b`. Case-insensitive per RFC 9562. Rejects the Nil
 * (`00000000-0000-0000-0000-000000000000`) and Max (`ffffffff-ffff-ffff-ffff-ffffffffffff`)
 * UUIDs, since neither can identify a real resource.
 *
 * @see {@link https://www.rfc-editor.org/rfc/rfc9562.html RFC 9562} — current UUID spec (obsoletes
 * RFC 4122): §4 string format/case rules, §5.9 Nil UUID, §5.10 Max UUID.
 * @see {@link https://www.rfc-editor.org/rfc/rfc4122.html RFC 4122} — original UUID spec: §3
 * string format, §4.1.1 variant field, §4.1.3 version field.
 */
const guidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Parses a GUID entity ID from a single route parameter token.
 * @returns The token unchanged (case preserved), or null when the token is missing, blank or
 * not a well-formed GUID.
 */
export function parseGuidFromUrlToken(token: string | null): string | null {
    if (token == null) {
        return null;
    }

    return guidPattern.test(token) ? token : null;
}

/**
 * Finds a GUID entity ID carried by the `paramName` route parameter anywhere at or below
 * `root`, searching depth-first — the first match wins.
 *
 * @param root Route snapshot to search from (and its descendants). Pass
 * `router.routerState.snapshot.root` to search the whole active route tree — needed when the
 * param may live on an ancestor route. Pass a specific route's own snapshot (e.g.
 * `this.route.snapshot`) when the param is known to live on that exact route — a leaf ID owned
 * by a single component.
 * @param paramName Name of the route parameter holding the ID, e.g. `'inviteCode'`.
 * @returns The ID, or null when no route in the searched subtree carries a valid one.
 */
export function findGuidRouteParam(
    root: ActivatedRouteSnapshot,
    paramName: string
): string | null {
    const id = parseGuidFromUrlToken(root.paramMap.get(paramName));

    if (id != null) {
        return id;
    }

    for (const child of root.children) {
        const childId = findGuidRouteParam(child, paramName);

        if (childId != null) {
            return childId;
        }
    }

    return null;
}

/**
 * Same lookup as `findGuidRouteParam`, for callers that only run inside a route whose
 * `guidParamGuard` already guaranteed the ID — so a missing one is a programming error rather
 * than a state to branch on.
 *
 * @param root Route snapshot to search from (and its descendants) — see
 * {@link findGuidRouteParam} for when to pass the full tree root vs. a specific route's own
 * snapshot. Whichever is passed here must be the route (or route tree) actually covered by the
 * corresponding `guidParamGuard`, or the "guaranteed" contract this throws on doesn't hold.
 * @param paramName Name of the route parameter holding the ID, e.g. `'inviteCode'`.
 * @param missingMessage Message for the error thrown when no route carries a valid ID.
 * @returns The ID.
 * @throws Error when called outside a route carrying a valid `paramName`.
 */
export function requireGuidRouteParam(
    root: ActivatedRouteSnapshot,
    paramName: string,
    missingMessage: string
): string {
    const id = findGuidRouteParam(root, paramName);

    if (id == null) {
        throw new Error(missingMessage);
    }

    return id;
}
